import { Component, ElementRef, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { WidgetService } from 'src/app/services/widget.service';
import { ConfigService } from 'src/app/services/config.service';
import { IdentityService } from 'src/app/services/identity.service';
import { TeamInfo } from 'src/app/services/teams.service';
import { MuxAgentService } from 'src/app/services/mux-agent.service';
import { DialogService } from '../../../services/dialog.service';

/**
 * Team Policy — the admin page (docs/plans/org-policy.md).
 *
 * One noun, `policy.json` per team, edited here and enforced at dispatch and
 * at the edit API. Tabs: Members (roles), Access (providers, models, groups,
 * credential slots), Agents (provisioning, member-editable fields, scope).
 * Usage and Audit are phase 2. Every control maps to a policy field; the
 * widget assembles the JSON and PUTs it whole.
 *
 * Access tab design (2026-08-28): the page reads as a sentence (what the
 * policy means), providers are cards that only expand when restricted, and
 * models are chosen by FAMILY ("Claude Opus") rather than by dated id. A
 * family entry is stored as `family:claude-opus`; the server matches it with
 * the same rule (`policy.py: model_family`) so a newly released opus id is
 * covered the day it ships. Exact ids remain valid for pinning.
 *
 * Non-admins get the read-only "what applies to me" summary instead.
 */

type Vendor = 'anthropic' | 'openai' | 'google' | 'ollama';
const VENDOR_LABELS: Record<string, string> = { anthropic: 'Claude', openai: 'OpenAI (ChatGPT / Codex)', google: 'Gemini', ollama: 'Local (Ollama)', deepseek: 'DeepSeek' };
const BACKEND_VENDOR: Record<string, Vendor> = {
  'claude-cli': 'anthropic', 'claude-interactive': 'anthropic', 'claude': 'anthropic',
  'chatgpt': 'openai', 'codex': 'openai',
  'gemini': 'google', 'gemini-cli': 'google', 'antigravity': 'google',
  'ollama': 'ollama',
};
const EDITABLE_GROUPS = ['system_prompt', 'model', 'credentials', 'mcp', 'skills', 'dirs', 'heartbeat', 'name'];
const SCOPE_MODES = ['read-only', 'workspace', 'full'];
const FAMILY_PREFIX = 'family:';
const DROP_TOKENS = new Set(['latest', 'preview', 'exp', 'v']);

/** Mirror of policy.py `model_family` — keep in lockstep. */
export function modelFamily(model: string): string {
  const out: string[] = [];
  for (let tok of (model || '').toLowerCase().split(/[^a-z0-9]+/)) {
    if (!tok || /^[0-9]/.test(tok)) continue;
    tok = tok.replace(/[0-9]+$/, '');
    if (DROP_TOKENS.has(tok)) continue;
    out.push(tok);
  }
  return out.join('-');
}
function familyLabel(key: string): string {
  return key.split('-').map((w) => (w === 'gpt' || w === 'oss' ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}
function entryMatches(model: string, entry: string): boolean {
  return entry.startsWith(FAMILY_PREFIX) ? entry.slice(FAMILY_PREFIX.length) === modelFamily(model) : entry === model;
}
function modelIn(model: string, entries: string[]): boolean { return entries.some((e) => entryMatches(model, e)); }

interface Family { key: string; label: string; ids: string[]; }
/** Per-vendor access: `entries === null` means unrestricted (vendor omitted from policy.models). */
type VendorAccess = Record<string, string[] | null>;
interface GroupRow {
  name: string;
  members: string;                          // comma-separated (policy fallback; SSO groups add to it)
  allow: Record<string, string[]>;          // vendor -> entries (family:… or exact id)
  deny: Record<string, string[]>;
  agents: Record<string, boolean>;
  dirs: string;                             // comma-separated, relative to /data/{team}
  slotsAllow: string[];                     // slot ids this group may bill to (empty = all team slots)
  slotsDeny: string[];                      // slot ids this group may never bill to (wins)
  mcpAllow: string[];                       // MCP server names this group may connect (empty = team default)
  mcpDeny: string[];                        // MCP server names this group may never connect (wins)
  more: boolean;                            // agents + dirs disclosure
}
interface SlotRow { id: string; vendor: string; mode: 'oauth' | 'api_key'; holder: 'user' | 'team'; required: boolean; default: boolean; }
interface MemberRow { username: string; role: 'owner' | 'admin' | 'member'; }
interface Clause { text: string; bold?: boolean; target?: string; }

@Component({
  selector: 'app-team-policy-widget',
  templateUrl: './team-policy-widget.component.html',
  styleUrls: ['../base-widget/base-widget.component.scss', './team-policy-widget.component.scss'],
  standalone: false,
})
export class TeamPolicyWidgetComponent extends BaseWidgetComponent implements OnInit {
  private dialogs = inject(DialogService);
  public title = 'Team Policy';
  public team = 'local';
  public tab: 'members' | 'access' | 'agents' | 'usage' | 'audit' = 'access';
  public _tabChosen = false;   // user clicked a tab — stop auto-selecting
  public loading = true;
  public saving = false;
  public error = '';
  public notice = '';

  // What the server says applies to ME (also tells us whether I'm an admin).
  public me: any = null;
  public isAdmin = false;
  public exists = false;

  // Catalogs
  public backends: string[] = [];                          // backend ids the server has
  public modelsByVendor: Record<string, string[]> = {};     // vendor -> model ids (union across backends)
  public familiesByVendor: Record<string, Family[]> = {};   // vendor -> families (derived from the catalog)
  public agents: MuxAgentService.Agent[] = [];
  public teamInfo: TeamInfo | null = null;

  // Editable state (mirrors policy.json)
  public members: MemberRow[] = [];
  // Usage tab (phases plan 1.5): the team ledger, rolled up.
  public usage: any = null;
  public usageBy: 'user' | 'model' | 'agent' | 'channel' | 'credential' = 'user';
  public readonly usageBys: Array<'user' | 'model' | 'agent' | 'channel' | 'credential'> = ['user', 'model', 'agent', 'channel', 'credential'];
  public usageRange: 'month' | '30d' | '90d' | 'year' | 'custom' = 'month';
  public readonly usageRanges: Array<{ key: 'month' | '30d' | '90d' | 'year' | 'custom'; label: string }> = [
    { key: 'month', label: 'this month' }, { key: '30d', label: 'last 30 days' }, { key: '90d', label: 'last 90 days' },
    { key: 'year', label: 'this year' }, { key: 'custom', label: 'custom…' },
  ];
  public usageSince = '';   // YYYY-MM-DD (custom range)
  public usageUntil = '';
  public usageLoading = false;
  // Audit tab (phases plan 2.1/2.2): activity feed over the ledger + the admin-action log.
  public auditView: 'activity' | 'actions' = 'activity';
  public activity: any = null;
  public auditEntries: any[] | null = null;
  public auditLoading = false;
  public auditError = '';
  public activityFilter: { user: string; agent: string; channel: string; model: string } = { user: '', agent: '', channel: '', model: '' };
  public auditActionFilter = '';
  public activitySource = '';
  public readonly sourceOptions = [
    { key: 'channel_reply', label: 'channel replies' }, { key: 'autonomous', label: 'autonomous turns' },
    { key: 'webapp', label: 'chat' }, { key: 'backfill', label: 'backfilled history' },
  ];
  public readonly actionOptions = [
    { key: 'policy_saved', label: 'policy saved' }, { key: 'policy_removed', label: 'policy removed' }, { key: 'policy_applied', label: 'policy applied to live sessions' },
    { key: 'support_view', label: 'viewed a member\'s agents' }, { key: 'support_edit', label: 'changed a member\'s agent' },
    { key: 'budget_alert', label: 'budget alert' }, { key: 'model_switched', label: 'model switched' }, { key: 'agent_stopped', label: 'agent stopped' }, { key: 'team_stopped', label: 'team stopped' },
    { key: 'offboard', label: 'offboarded' }, { key: 'offboard_archived', label: 'offboarding archived' }, { key: 'offboard_reinstated', label: 'reinstated' },
    { key: 'transcript_grant', label: 'transcript window opened' }, { key: 'transcript_view', label: 'transcript read' }, { key: 'transcript_revoked', label: 'transcript window closed' },
    { key: 'retention_pruned', label: 'retention pruned records' },
  ];
  // Budgets (phases plan 1.4): usage.budgets on the policy; edited on the Usage tab, saved with the policy.
  public budgets: { team: string; user: string; onExceed: 'warn' | 'refuse' | 'switch'; alerts: string; groups: Record<string, string>;
                    fallbackBackend: string; fallbackModel: string; fallbackCred: string } =
    { team: '', user: '', onExceed: 'warn', alerts: '50, 80, 100', groups: {}, fallbackBackend: '', fallbackModel: '', fallbackCred: '' };
  /** Backends a used-up budget can switch a turn to (the server builds these on demand). */
  public readonly fallbackBackends = ['gemini-cli', 'antigravity', 'chatgpt'];
  public usageError = '';
  public memberWildcard = true;                             // roles.member == "*"
  public backendAllowed: Record<string, boolean> = {};
  public defaultAccess: VendorAccess = {};                  // vendor -> entries | null (unrestricted)
  public groups: GroupRow[] = [];
  public slots: SlotRow[] = [];
  /** Team-held slots: {slot id → status} from GET /teams/{t}/credentials (never the key). */
  public teamKeys: Record<string, any> = {};
  public savedSlotIds: string[] = [];
  public memberOwnedAllowed = false;
  public provisionedBy: 'admin' | 'member' = 'admin';
  public defaults: Record<string, boolean> = {};
  public memberEditable: Record<string, boolean> = {};
  public defaultScopeMode = 'workspace';
  public agentScopes: Record<string, { mode: string; mcp: string; tools: string; dirs: string }> = {};
  // Integrations (phases plan 1.1): team-wide MCP allow-list (null = any) + deny-list; names offered as chips.
  public mcpAllowed: string[] | null = null;
  public mcpDenied: string[] = [];
  public mcpNames: string[] = [];
  public newMcpName = '';
  // Telemetry + network (phases plan 2.3): OTel export for Claude sessions; proxy / gateway base URLs.
  public telemetry: { endpoint: string; protocol: 'grpc' | 'http'; headers: string; metrics: boolean; logs: boolean } =
    { endpoint: '', protocol: 'grpc', headers: '', metrics: true, logs: true };
  public network: { httpsProxy: string; noProxy: string; anthropicBaseUrl: string; openaiBaseUrl: string } =
    { httpsProxy: '', noProxy: '', anthropicBaseUrl: '', openaiBaseUrl: '' };
  /** Offboarding (3.1): who inherits a leaver's work, what moves, when the rest is archived. */
  public offboarding: { successor: string; agents: boolean; channels: boolean; archiveDays: string; idp: boolean } =
    { successor: '', agents: true, channels: true, archiveDays: '30', idp: true };
  /** Per-person successors (member → member or group); wins over the team-wide successor. */
  public successors: { member: string; successor: string }[] = [];
  /** Excluded paths (3.2): one pattern per line; written as Read/Edit deny rules into every Claude profile the team spawns. */
  public excludedPaths = '';
  /** Retention + holds (3.3): audit log + usage ledger rows past retain_days are pruned daily; people on hold are never pruned. */
  public retainDays = '';
  public grantMaxHours = '72';
  public holds: { user: string; reason: string; since?: number; by?: string }[] = [];
  public isOwner = false;
  /** Transcript access (3.3): the live grant this admin holds on the selected member, and the viewer. */
  public grant: any = null;
  public grantMeta: { max_hours: number; can_quiet: boolean } = { max_hours: 72, can_quiet: false };
  public grantForm: { hours: string; reason: string; notify: boolean } = { hours: '4', reason: '', notify: true };
  public transcriptChannels: string[] = [];
  public transcriptChannel = '';
  public transcriptMessages: any[] = [];
  public transcriptNotice = '';
  public transcriptLoading = false;

  // View state
  public showVersions: Record<string, boolean> = {};        // vendor -> pin-by-id disclosure open
  public viewAs = '';                                       // '' = everyone else, else a group name

  // Support console (Agents tab, 2026-09-01): admins inspect and fix a
  // MEMBER's agents — config only, never memory or transcripts; every view
  // and edit lands in the team audit log and on the member's own summary.
  public teamMembers: any[] = [];   // directory rows: role+source, groups+source, slot sign-ins, last_active
  public supportMember = '';
  public supportAgents: any[] = [];
  public supportLoading = false;
  public supportNotice = '';
  public supportSelected: any = null;   // drill-down: the one agent whose controls are open

  public readonly vendorLabels = VENDOR_LABELS;
  public readonly editableGroups = EDITABLE_GROUPS;
  public readonly scopeModes = SCOPE_MODES;

  constructor(
    private _widgetService: WidgetService,
    private _elRef: ElementRef,
    private http: HttpClient,
    private identity: IdentityService,
    private agentService: MuxAgentService,
  ) {
    super();
  }

  get vendors(): string[] { return Object.keys(this.modelsByVendor); }
  get enabledVendors(): string[] { return this.vendors.filter((v) => this.vendorEnabled(v)); }

  async ngOnInit(): Promise<void> {
    // Opened for a specific team from the palette (baseInputs.teamName) or for the tab's team.
    this.team = this.teamName || this.identity.me?.selectedTeamName || 'local';
    this.title = `Team Policy · ${this.team}`;
    await this.reload();
  }

  private mux(path: string): string { return `${ConfigService.muxUrl}${path}`; }

  async reload(): Promise<void> {
    this.loading = true; this.error = ''; this.notice = '';
    try {
      const [me, models, agents, mcpList] = await Promise.all([
        lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/policy/me`))),
        lastValueFrom(this.http.get<any>(this.mux('/models'))),          // unfiltered catalog for the editor
        this.agentService.loadAgents(this.team),
        lastValueFrom(this.http.get<any>(this.mux('/mcp/servers'))).catch(() => null),
      ]);
      this.me = me;
      const names = new Set<string>();
      for (const a of (agents || [])) for (const n of (a.mcp_servers || [])) names.add(n);
      for (const sv of (mcpList?.servers || [])) if (sv?.name) names.add(sv.name);
      for (const n of this.mcpNames) names.add(n);
      this.mcpNames = Array.from(names).sort();
      this.isAdmin = me?.role === 'owner' || me?.role === 'admin';
      this.isOwner = me?.role === 'owner';
      const hadLoaded = this.exists;
      this.exists = !!me?.exists;
      // First open: a governed team lands on the people directory; a team with
      // no policy lands on Access, where Create policy lives.
      if (!hadLoaded && !this._tabChosen) this.tab = this.exists ? 'members' : 'access';
      this.agents = agents || [];
      this.backends = Object.keys(models?.models || {});
      this.modelsByVendor = {};
      for (const b of this.backends) {
        const v = BACKEND_VENDOR[b] || b;
        const list = (models.models[b] || []).map((m: any) => m.name || String(m));
        this.modelsByVendor[v] = Array.from(new Set([...(this.modelsByVendor[v] || []), ...list]));
      }
      this.familiesByVendor = {};
      for (const v of this.vendors) {
        const byKey = new Map<string, Family>();
        for (const id of this.modelsByVendor[v]) {
          const key = modelFamily(id);
          if (!byKey.has(key)) byKey.set(key, { key, label: familyLabel(key), ids: [] });
          byKey.get(key)!.ids.push(id);
        }
        this.familiesByVendor[v] = Array.from(byKey.values());
      }
      try {
        // Not TeamsService.load(): it returns a Subject that never completes, so
        // lastValueFrom() on it hangs the whole reload (2026-08-27).
        const orgs = await lastValueFrom(this.http.get<TeamInfo[]>(this.mux('/organizations')));
        this.teamInfo = (orgs || []).find((t) => t.slug === this.team || t.name === this.team) || null;
      } catch { this.teamInfo = null; }

      if (this.isAdmin) {
        const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/policy`)));
        this.fromPolicy(res?.policy || null);
        this.loadMembers();     // support console picker (fire-and-forget)
      }
    } catch (e: any) {
      this.error = e?.error?.detail || e?.message || 'Failed to load policy';
    } finally {
      this.loading = false;
    }
  }

  // ---- policy.json <-> editable state ----------------------------------------

  private fromPolicy(p: any | null): void {
    const pol = p || {};
    const roles = pol.roles || {};
    const rows = new Map<string, MemberRow>();
    for (const u of (this.teamInfo?.members || [])) rows.set(u, { username: u, role: 'member' });
    for (const r of ['member', 'admin', 'owner'] as const) {
      const v = roles[r];
      if (Array.isArray(v)) for (const u of v) rows.set(u, { username: u, role: r });
    }
    this.memberWildcard = roles.member === '*' || roles.member === undefined;
    // Never let the person creating the policy save themselves out of it: with
    // no owner/admin named yet, the current user is the owner (server enforces
    // the same guard on PUT).
    const meName = this.identity.me?.username;
    if (meName && !Array.from(rows.values()).some((r) => r.role !== 'member')) {
      rows.set(meName, { username: meName, role: 'owner' });
    }
    this.members = Array.from(rows.values()).sort((a, b) => a.username.localeCompare(b.username));

    const backends = pol.backends || {};
    this.backendAllowed = {};
    for (const b of this.backends) this.backendAllowed[b] = backends.allowed ? backends.allowed.includes(b) : true;
    this.defaultAccess = {};
    for (const v of this.vendors) {
      const allowed: string[] | undefined = (backends.models || {})[v] ?? (backends.models || {})[this.alias(v)];
      this.defaultAccess[v] = allowed ? [...allowed] : null;
    }

    this.groups = [];
    for (const [name, g] of Object.entries<any>(pol.groups || {})) {
      const row: GroupRow = { name, members: (g.members || []).join(', '), allow: {}, deny: {}, agents: {}, dirs: (g.dirs || []).join(', '), slotsAllow: [...((g.slots || {}).allow || [])], slotsDeny: [...((g.slots || {}).deny || [])], mcpAllow: [...(((g.mcp || {}).allow) || [])], mcpDeny: [...(((g.mcp || {}).deny) || [])], more: false };
      for (const v of this.vendors) {
        row.allow[v] = [...((g.allow || {})[v] ?? (g.allow || {})[this.alias(v)] ?? [])];
        row.deny[v] = [...((g.deny || {})[v] ?? (g.deny || {})[this.alias(v)] ?? [])];
      }
      for (const a of this.agents) row.agents[a.id] = (g.agents || []).includes(a.id);
      row.more = !!(row.dirs || Object.values(row.agents).some(Boolean));
      this.groups.push(row);
    }

    const integ = ((pol.integrations || {}).mcp) || {};
    this.mcpAllowed = Array.isArray(integ.allowed) ? [...integ.allowed] : null;
    this.mcpDenied = [...(integ.denied || [])];
    for (const n of [...(this.mcpAllowed || []), ...this.mcpDenied, ...this.groups.flatMap((g) => [...g.mcpAllow, ...g.mcpDeny])]) {
      if (n && !this.mcpNames.includes(n)) this.mcpNames.push(n);
    }
    this.mcpNames.sort();
    const tel = pol.telemetry || {};
    const hdrs = tel.headers && typeof tel.headers === 'object' ? Object.entries<any>(tel.headers).map(([k, v]) => `${k}=${v}`).join(', ') : (tel.headers || '');
    this.telemetry = { endpoint: tel.otlp_endpoint || '', protocol: tel.protocol === 'http' ? 'http' : 'grpc', headers: hdrs,
                       metrics: tel.metrics !== false, logs: tel.logs !== false };
    const net = pol.network || {};
    this.network = { httpsProxy: net.https_proxy || '', noProxy: net.no_proxy || '', anthropicBaseUrl: net.anthropic_base_url || '', openaiBaseUrl: net.openai_base_url || '' };
    const off = pol.offboarding || {};
    const tr: string[] = Array.isArray(off.transfer) ? off.transfer : ['agents', 'channels'];
    this.offboarding = { successor: off.successor || '', agents: tr.includes('agents'), channels: tr.includes('channels'),
                         archiveDays: off.archive_after_days != null ? String(off.archive_after_days) : '30', idp: off.idp !== false };
    this.successors = Object.entries<any>(off.successors || {}).map(([member, successor]) => ({ member, successor: String(successor) }));
    this.excludedPaths = (((pol.data || {}).excluded_paths) || []).join('\n');
    const aud = pol.audit || {};
    this.retainDays = aud.retain_days != null ? String(aud.retain_days) : '';
    this.grantMaxHours = aud.transcript_grant_max_hours != null ? String(aud.transcript_grant_max_hours) : '72';
    this.holds = (aud.holds || []).filter((h: any) => h && h.user).map((h: any) => ({ user: h.user, reason: h.reason || '', since: h.since, by: h.by }));
    const bud = ((pol.usage || {}).budgets) || {};
    this.budgets = {
      team: bud.team_monthly_usd != null ? String(bud.team_monthly_usd) : '',
      user: bud.per_user_monthly_usd != null ? String(bud.per_user_monthly_usd) : '',
      onExceed: bud.on_exceed === 'refuse' ? 'refuse' : (bud.on_exceed === 'switch' ? 'switch' : 'warn'),
      alerts: Array.isArray(bud.alerts) && bud.alerts.length ? bud.alerts.join(', ') : '50, 80, 100',
      groups: Object.fromEntries(Object.entries<any>(bud.groups || {}).map(([g, v]) => [g, String(v)])),
      fallbackBackend: (bud.fallback || {}).backend || '',
      fallbackModel: (bud.fallback || {}).model || '',
      fallbackCred: (bud.fallback || {}).credentials_id || '',
    };
    const creds = pol.credentials || {};
    this.slots = (creds.slots || []).map((s: any) => ({ id: s.id, vendor: this.canon(s.vendor), mode: s.mode || 'oauth', holder: s.holder || 'user', required: !!s.required, default: !!s.default }));
    this.savedSlotIds = this.slots.map((s) => s.id);
    if (this.slots.some((s) => s.holder === 'team')) this.loadTeamKeys(); else this.teamKeys = {};
    for (const v of new Set(this.slots.map((s) => s.vendor))) {   // server rule: unmarked → first listed is the default
      const mine = this.slots.filter((s) => s.vendor === v);
      if (!mine.some((s) => s.default)) mine[0].default = true;
    }
    this.memberOwnedAllowed = creds.member_owned_allowed !== undefined ? !!creds.member_owned_allowed : !p;

    const ag = pol.agents || {};
    this.provisionedBy = ag.provisioned_by || (p ? 'admin' : 'member');
    this.defaults = {};
    for (const a of this.agents) this.defaults[a.id] = (ag.defaults || []).includes(a.id);
    this.memberEditable = {};
    for (const f of EDITABLE_GROUPS) this.memberEditable[f] = ag.member_editable ? ag.member_editable.includes(f) : true;
    this.defaultScopeMode = ag.default_scope_mode || (p ? 'workspace' : 'full');
    this.agentScopes = {};
    for (const a of this.agents) {
      const sc = (ag.scope || {})[a.id] || {};
      this.agentScopes[a.id] = {
        mode: sc.mode || '',
        mcp: Array.isArray(sc.mcp) ? sc.mcp.join(', ') : '',
        tools: sc.tools === 'read-only' ? 'read-only' : Array.isArray(sc.tools) ? sc.tools.join(', ') : '',
        dirs: sc.dirs || 'channel-workspace',
      };
    }
  }

  public toPolicy(): any {
    const roles: any = {};
    const by = (r: string) => this.members.filter((m) => m.role === r).map((m) => m.username);
    if (by('owner').length) roles.owner = by('owner');
    if (by('admin').length) roles.admin = by('admin');
    roles.member = this.memberWildcard ? '*' : by('member');

    const models: any = {};
    for (const v of this.vendors) {
      const entries = this.defaultAccess[v];
      if (entries !== null && entries !== undefined) models[v] = entries;   // unrestricted vendors are omitted
    }
    const allowedBackends = this.backends.filter((b) => this.backendAllowed[b]);
    const backends: any = { models };
    if (allowedBackends.length !== this.backends.length) backends.allowed = allowedBackends;

    const groups: any = {};
    for (const g of this.groups) {
      if (!g.name.trim()) continue;
      const entry: any = {};
      const allow: any = {}, deny: any = {};
      for (const v of this.vendors) {
        if (g.allow[v]?.length) allow[v] = g.allow[v];
        if (g.deny[v]?.length) deny[v] = g.deny[v];
      }
      if (Object.keys(allow).length) entry.allow = allow;
      if (Object.keys(deny).length) entry.deny = deny;
      const agents = this.agents.filter((a) => g.agents[a.id]).map((a) => a.id);
      if (agents.length) entry.agents = agents;
      const members = this.splitList(g.members);
      if (members.length) entry.members = members;
      const dirs = this.splitList(g.dirs);
      if (dirs.length) entry.dirs = dirs;
      const slotIds = new Set(this.slots.map((s) => s.id.trim()));
      const sa = g.slotsAllow.filter((x) => slotIds.has(x)), sd = g.slotsDeny.filter((x) => slotIds.has(x));
      if (sa.length || sd.length) entry.slots = { ...(sa.length ? { allow: sa } : {}), ...(sd.length ? { deny: sd } : {}) };
      if (g.mcpAllow.length || g.mcpDeny.length) entry.mcp = { ...(g.mcpAllow.length ? { allow: g.mcpAllow } : {}), ...(g.mcpDeny.length ? { deny: g.mcpDeny } : {}) };
      groups[g.name.trim()] = entry;
    }

    const agentsBlock: any = {
      provisioned_by: this.provisionedBy,
      defaults: this.agents.filter((a) => this.defaults[a.id]).map((a) => a.id),
      member_editable: EDITABLE_GROUPS.filter((f) => this.memberEditable[f]),
      default_scope_mode: this.defaultScopeMode,
    };
    const scope: any = {};
    for (const a of this.agents) {
      const s = this.agentScopes[a.id];
      if (!s || !s.mode) continue;
      const entry: any = { mode: s.mode, dirs: s.dirs || 'channel-workspace' };
      if (s.mcp.trim()) entry.mcp = this.splitList(s.mcp);
      if (s.tools.trim()) entry.tools = s.tools.trim() === 'read-only' ? 'read-only' : this.splitList(s.tools);
      scope[a.id] = entry;
    }
    if (Object.keys(scope).length) agentsBlock.scope = scope;

    return {
      version: 1,
      roles,
      backends,
      groups,
      integrations: { mcp: { allowed: this.mcpAllowed === null ? 'all' : this.mcpAllowed, denied: this.mcpDenied } },
      ...(this.budgetsBlock() ? { usage: { budgets: this.budgetsBlock() } } : {}),
      ...(this.telemetry.endpoint.trim() ? { telemetry: { otlp_endpoint: this.telemetry.endpoint.trim(), protocol: this.telemetry.protocol,
            ...(this.telemetry.headers.trim() ? { headers: this.telemetry.headers.trim() } : {}), metrics: this.telemetry.metrics, logs: this.telemetry.logs } } : {}),
      ...(Object.values(this.network).some((v) => v.trim()) ? { network: {
            ...(this.network.httpsProxy.trim() ? { https_proxy: this.network.httpsProxy.trim() } : {}),
            ...(this.network.noProxy.trim() ? { no_proxy: this.network.noProxy.trim() } : {}),
            ...(this.network.anthropicBaseUrl.trim() ? { anthropic_base_url: this.network.anthropicBaseUrl.trim() } : {}),
            ...(this.network.openaiBaseUrl.trim() ? { openai_base_url: this.network.openaiBaseUrl.trim() } : {}) } } : {}),
      ...(this.offboardingBlock() ? { offboarding: this.offboardingBlock() } : {}),
      ...(this.excludedPathList().length ? { data: { excluded_paths: this.excludedPathList() } } : {}),
      ...(this.auditBlock() ? { audit: this.auditBlock() } : {}),
      credentials: {
        slots: this.slots.filter((s) => s.id.trim() && s.vendor).map((s) => ({ id: s.id.trim(), vendor: s.vendor, mode: s.mode, holder: s.holder, required: s.required, default: s.default })),
        member_owned_allowed: this.memberOwnedAllowed,
      },
      agents: agentsBlock,
    };
  }

  async save(): Promise<void> {
    this.saving = true; this.error = ''; this.notice = '';
    try {
      const body = this.toPolicy();
      await lastValueFrom(this.http.put<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/policy`), body));
      this.notice = this.exists ? 'Policy saved. It applies on the next message.' : 'Policy created. This team is now governed by it.';
      await this.reload();
    } catch (e: any) {
      this.error = e?.error?.detail || e?.message || 'Save failed';
    } finally {
      this.saving = false;
    }
  }

  async remove(): Promise<void> {
    if (!await this.dialogs.confirm(`Remove the policy for ${this.team}? The team goes back to behaving as it did before any policy.`)) return;
    this.saving = true; this.error = ''; this.notice = '';
    try {
      await lastValueFrom(this.http.delete<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/policy`)));
      this.notice = 'Policy removed. This team behaves as before.';
      await this.reload();
    } catch (e: any) {
      this.error = e?.error?.detail || e?.message || 'Remove failed';
    } finally {
      this.saving = false;
    }
  }

  // ---- providers & families ----------------------------------------------------

  backendsOf(vendor: string): string[] { return this.backends.filter((b) => (BACKEND_VENDOR[b] || b) === vendor); }
  vendorEnabled(vendor: string): boolean { return this.backendsOf(vendor).some((b) => this.backendAllowed[b]); }
  toggleVendor(vendor: string): void {
    const on = !this.vendorEnabled(vendor);
    for (const b of this.backendsOf(vendor)) this.backendAllowed[b] = on;
  }
  isRestricted(vendor: string): boolean { return this.defaultAccess[vendor] !== null && this.defaultAccess[vendor] !== undefined; }
  setRestricted(vendor: string, on: boolean): void {
    if (on && !this.isRestricted(vendor)) this.defaultAccess[vendor] = [];
    if (!on) { this.defaultAccess[vendor] = null; this.showVersions[vendor] = false; }
  }

  familyEntry(key: string): string { return FAMILY_PREFIX + key; }
  has(list: string[] | null | undefined, entry: string): boolean { return !!list && list.includes(entry); }
  flip(list: string[], entry: string): void {
    const i = list.indexOf(entry);
    if (i >= 0) list.splice(i, 1); else list.push(entry);
  }
  /** A pinned id is implied when its family is selected. */
  idImplied(list: string[] | null | undefined, id: string): boolean { return this.has(list, FAMILY_PREFIX + modelFamily(id)); }
  /** Chips for a vendor's whole family list, or the ones present in `list`. */
  entryLabel(entry: string): string { return entry.startsWith(FAMILY_PREFIX) ? familyLabel(entry.slice(FAMILY_PREFIX.length)) : entry; }
  describeList(list: string[] | null | undefined): string {
    if (list === null || list === undefined) return 'any model';
    if (!list.length) return 'no models';
    return list.map((e) => this.entryLabel(e)).join(', ');
  }

  // ---- the sentence + view-as ------------------------------------------------------

  /** The policy, read aloud. Every bold clause jumps to its control. */
  get sentence(): Clause[] {
    const out: Clause[] = [];
    const enabled = this.vendors.filter((v) => this.vendorEnabled(v));
    out.push({ text: `Members of ${this.team} ` });
    if (!enabled.length) { out.push({ text: 'reach no providers', bold: true, target: 'tp-providers' }, { text: '.' }); return out; }
    out.push({ text: 'reach ' });
    enabled.forEach((v, i) => {
      out.push({ text: VENDOR_LABELS[v]?.split(' (')[0] || v, bold: true, target: 'tp-providers' });
      const vs = this.slots.filter((s) => s.vendor === v && s.id.trim());
      if (vs.length) out.push({ text: ' ' + this.reachPhrase(vs), bold: true, target: 'tp-slots' });
      out.push({ text: i < enabled.length - 2 ? ', ' : i === enabled.length - 2 ? ' and ' : '' });
    });
    const restricted = enabled.filter((v) => this.isRestricted(v));
    if (!restricted.length) {
      out.push({ text: ' and may use ' }, { text: 'any model', bold: true, target: 'tp-providers' }, { text: '.' });
    } else {
      out.push({ text: ' and may use ' });
      const names = enabled.map((v) => this.describeList(this.defaultAccess[v]));
      out.push({ text: names.join('; '), bold: true, target: 'tp-providers' }, { text: '.' });
    }
    if (this.mcpAllowed === null && !this.mcpDenied.length) {
      out.push({ text: ' Agents may connect ' }, { text: 'any integration', bold: true, target: 'tp-integrations' }, { text: '.' });
    } else {
      out.push({ text: ' Agents may connect ' }, { text: this.mcpAllowed === null ? 'any integration' : (this.mcpAllowed.join(', ') || 'no integrations'), bold: true, target: 'tp-integrations' });
      if (this.mcpDenied.length) out.push({ text: ' but never ' }, { text: this.mcpDenied.join(', '), bold: true, target: 'tp-integrations' });
      out.push({ text: '.' });
    }
    if (this.telemetry.endpoint.trim()) out.push({ text: ' Sessions report telemetry to ' }, { text: this.telemetry.endpoint.trim(), bold: true, target: 'tp-network' }, { text: '.' });
    if (this.network.httpsProxy.trim()) out.push({ text: ' Traffic leaves through ' }, { text: this.network.httpsProxy.trim(), bold: true, target: 'tp-network' }, { text: '.' });
    if (this.offboarding.successor.trim()) out.push({ text: ' When someone leaves, their work goes to ' }, { text: this.offboarding.successor.trim(), bold: true, target: 'tp-offboarding' }, { text: this.offboarding.idp ? ' (also when the IdP disables them).' : '.' });
    if (parseInt(this.retainDays, 10) > 0) out.push({ text: ' Audit and usage records are kept for ' }, { text: `${parseInt(this.retainDays, 10)} days`, bold: true, target: 'tp-retention' }, { text: this.holds.length ? ` (${this.holds.length} on legal hold).` : '.' });
    if (this.excludedPathList().length) out.push({ text: ' Sessions never read ' }, { text: this.excludedPathList().join(', '), bold: true, target: 'tp-data' }, { text: '.' });
    if (this.network.anthropicBaseUrl.trim() || this.network.openaiBaseUrl.trim()) out.push({ text: ' Model calls go via ' }, { text: [this.network.anthropicBaseUrl.trim(), this.network.openaiBaseUrl.trim()].filter(Boolean).join(' and '), bold: true, target: 'tp-network' }, { text: '.' });
    for (const g of this.groups) {
      if (!g.name.trim()) continue;
      const allows = this.vendors.flatMap((v) => g.allow[v] || []).map((e) => this.entryLabel(e));
      const denies = this.vendors.flatMap((v) => g.deny[v] || []).map((e) => this.entryLabel(e));
      if (allows.length) out.push({ text: ' ' }, { text: g.name, bold: true, target: 'tp-groups' }, { text: ' may use ' }, { text: allows.join(', '), bold: true, target: 'tp-groups' }, { text: '.' });
      if (denies.length) out.push({ text: ' ' }, { text: g.name, bold: true, target: 'tp-groups' }, { text: ' may never use ' }, { text: denies.join(', '), bold: true, target: 'tp-groups' }, { text: '.' });
    }
    for (const g of this.groups) {
      if (!g.name.trim()) continue;
      if (g.slotsAllow.length) out.push({ text: ' ' }, { text: g.name, bold: true, target: 'tp-groups' }, { text: ' bill to ' }, { text: g.slotsAllow.join(', '), bold: true, target: 'tp-slots' }, { text: ' only.' });
      if (g.mcpAllow.length) out.push({ text: ' ' }, { text: g.name, bold: true, target: 'tp-groups' }, { text: ' may connect ' }, { text: g.mcpAllow.join(', '), bold: true, target: 'tp-groups' }, { text: '.' });
      if (g.mcpDeny.length) out.push({ text: ' ' }, { text: g.name, bold: true, target: 'tp-groups' }, { text: ' may never connect ' }, { text: g.mcpDeny.join(', '), bold: true, target: 'tp-groups' }, { text: '.' });
      if (g.slotsDeny.length) out.push({ text: ' ' }, { text: g.name, bold: true, target: 'tp-groups' }, { text: ' never bill to ' }, { text: g.slotsDeny.join(', '), bold: true, target: 'tp-slots' }, { text: '.' });
    }
    if (!this.memberOwnedAllowed) out.push({ text: ' Members ' }, { text: 'cannot add their own credentials', bold: true, target: 'tp-slots' }, { text: '.' });
    if (this.provisionedBy === 'admin') out.push({ text: ' Only ' }, { text: 'admins create agents', bold: true, target: 'tp-agent-rules' }, { text: '.' });
    const editable = EDITABLE_GROUPS.filter((f) => this.memberEditable[f]);
    if (editable.length && editable.length < EDITABLE_GROUPS.length) {
      out.push({ text: ' Members may edit ' }, { text: editable.join(', '), bold: true, target: 'tp-agent-rules' }, { text: ' on their agents.' });
    } else if (!editable.length) {
      out.push({ text: ' Members ' }, { text: 'may not edit their agents', bold: true, target: 'tp-agent-rules' }, { text: '.' });
    }
    return out;
  }

  /** "with their own sign-in on positronic (default), abodoo or jovai" */
  private reachPhrase(vs: SlotRow[]): string {
    const how = (s: SlotRow) => s.mode === 'oauth' ? 'their own sign-in' : (s.holder === 'team' ? 'a team key' : 'their own key');
    if (vs.length === 1) return `with ${how(vs[0])} (${vs[0].id.trim()})`;
    const hows = new Set(vs.map(how));
    const names = vs.map((s) => s.id.trim() + (s.default ? ' (default)' : ''));
    const list = names.length > 1 ? names.slice(0, -1).join(', ') + ' or ' + names[names.length - 1] : names[0];
    return hows.size === 1 ? `with ${[...hows][0]} on ${list}` : `on ${list}`;
  }

  /** Slot ids a member of `viewAs` may bill to — mirrors policy.py effective_slots. */
  get previewSlots(): string[] | null {
    const ids = this.slots.filter((s) => s.id.trim()).map((s) => s.id.trim());
    if (!ids.length) return null;
    const g = this.groups.find((x) => x.name.trim() === this.viewAs);
    const allowed = g?.slotsAllow.length ? g.slotsAllow : ids;
    const denied = new Set(g?.slotsDeny || []);
    return allowed.filter((x) => ids.includes(x) && !denied.has(x));
  }

  /** Integrations a member of `viewAs` may connect — mirrors policy.py effective_mcp. */
  get previewMcp(): { allowed: string[] | null; denied: string[] } {
    const g = this.groups.find((x) => x.name.trim() === this.viewAs);
    const denied = Array.from(new Set([...this.mcpDenied, ...(g?.mcpDeny || [])]));
    const base = g?.mcpAllow.length ? g.mcpAllow : this.mcpAllowed;
    return { allowed: base === null ? null : base.filter((n) => !denied.includes(n)), denied };
  }
  isMcpRestricted(): boolean { return this.mcpAllowed !== null; }
  setMcpRestricted(on: boolean): void { if (on && this.mcpAllowed === null) this.mcpAllowed = []; if (!on) this.mcpAllowed = null; }
  addMcpName(): void {
    const n = this.newMcpName.trim();
    if (!n) return;
    if (!this.mcpNames.includes(n)) { this.mcpNames.push(n); this.mcpNames.sort(); }
    if (this.mcpAllowed !== null && !this.mcpAllowed.includes(n)) this.mcpAllowed.push(n);
    this.newMcpName = '';
  }
  /** usage.budgets for the policy body, or null when nothing is set. */
  budgetsBlock(): any | null {
    const num = (v: string): number | null => { const n = parseFloat((v || '').replace(/[$,\s]/g, '')); return isFinite(n) && n > 0 ? n : null; };
    const groups: Record<string, number> = {};
    for (const g of this.groupNames) { const n = num(this.budgets.groups[g] || ''); if (n) groups[g] = n; }
    const team = num(this.budgets.team), user = num(this.budgets.user);
    if (!team && !user && !Object.keys(groups).length) return null;
    const alerts = this.budgets.alerts.split(/[,\s]+/).map((x) => parseInt(x, 10)).filter((x) => x > 0 && x <= 100);
    return {
      ...(team ? { team_monthly_usd: team } : {}),
      ...(user ? { per_user_monthly_usd: user } : {}),
      ...(Object.keys(groups).length ? { groups } : {}),
      on_exceed: this.budgets.onExceed,
      alerts: alerts.length ? Array.from(new Set(alerts)).sort((a, b) => a - b) : [50, 80, 100],
      ...(this.budgets.onExceed === 'switch' ? { fallback: {
        backend: this.budgets.fallbackBackend,
        ...(this.budgets.fallbackModel.trim() ? { model: this.budgets.fallbackModel.trim() } : {}),
        ...(this.budgets.fallbackCred ? { credentials_id: this.budgets.fallbackCred } : {}),
      } } : {}),
    };
  }
  /** Cost share of the largest row, for the inline bar on the Usage table. */
  usageShare(r: any): number {
    const max = Math.max(0, ...((this.usage?.rows || []).map((x: any) => x.cost_usd || 0)));
    return max > 0 ? Math.round(100 * (r.cost_usd || 0) / max) : 0;
  }
  public usageByUser: any = null;
  /** Each budgeted group's month-to-date against its limit: members from the policy lists, spend from the by-user rows. */
  get groupBudgets(): Array<{ name: string; limit: number; spent: number; pct: number }> {
    const b = this.budgetsBlock();
    const src = this.usageByUser || (this.usageBy === 'user' ? this.usage : null);
    if (!b?.groups || !src || this.usageRange !== 'month') return [];
    const out: Array<{ name: string; limit: number; spent: number; pct: number }> = [];
    for (const [name, limit] of Object.entries<any>(b.groups)) {
      const lim = Number(limit); if (!lim) continue;
      const members = new Set(this.splitList(this.groups.find((g) => g.name === name)?.members || ''));
      const spent = (src.rows || []).filter((r: any) => members.has(r.key)).reduce((s: number, r: any) => s + (r.cost_usd || 0), 0);
      out.push({ name, limit: lim, spent, pct: Math.round(100 * spent / lim) });
    }
    return out;
  }
  get teamBudget(): { limit: number; spent: number; pct: number } | null {
    const b = this.budgetsBlock();
    if (!b?.team_monthly_usd || !this.usage || this.usageRange !== 'month') return null;
    const spent = this.usage.totals?.cost_usd || 0;
    return { limit: b.team_monthly_usd, spent, pct: Math.round(100 * spent / b.team_monthly_usd) };
  }
  /** Team month-to-date against the team budget, for the Usage tab header. */
  get teamBudgetLine(): string | null {
    const b = this.budgetsBlock();
    if (!b?.team_monthly_usd || !this.usage || this.usageRange !== 'month') return null;
    const spent = this.usage.totals?.cost_usd || 0;
    return `${this.fmtUsd(spent)} of ${this.fmtUsd(b.team_monthly_usd)} (${Math.round(100 * spent / b.team_monthly_usd)}%) this month`;
  }
  // ---- audit tab -----------------------------------------------------------------
  async loadAudit(): Promise<void> {
    if (this.usageRange === 'custom' && !this.usageSince) return;
    this.auditLoading = true; this.auditError = '';
    try {
      if (this.auditView === 'activity') {
        const f: Record<string, string> = { limit: '300' };
        for (const [k, v] of Object.entries(this.activityFilter)) if (v.trim()) f[k] = v.trim();
        this.activity = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/activity?${this.usageQuery(f)}`)));
      } else {
        const f: Record<string, string> = { limit: '300' };
        if (this.auditActionFilter) f['action'] = this.auditActionFilter;
        const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/audit?${this.usageQuery(f)}`)));
        this.auditEntries = res?.entries || [];
      }
    } catch (e: any) {
      this.auditError = e?.error?.detail || e?.message || 'Failed to load audit';
    } finally {
      this.auditLoading = false;
    }
  }
  setAuditView(v: 'activity' | 'actions'): void { this.auditView = v; this.loadAudit(); }
  // -- client-side sorting for the three tables --
  public sort: Record<'activity' | 'actions' | 'usage', { key: string; dir: 1 | -1 }> = {
    activity: { key: 'ts', dir: -1 }, actions: { key: 'ts', dir: -1 }, usage: { key: 'cost_usd', dir: -1 },
  };
  public readonly activityCols = [
    { key: 'ts', label: 'when' }, { key: 'user', label: 'person' }, { key: 'agent', label: 'agent' }, { key: 'channel', label: 'channel' },
    { key: 'model', label: 'model' }, { key: 'in', label: 'in', num: true }, { key: 'out', label: 'out', num: true },
    { key: 'cache', label: 'cached', num: true }, { key: 'cost_usd', label: 'est. cost', num: true }, { key: 'source', label: 'source' },
  ];
  public readonly actionCols = [{ key: 'ts', label: 'when' }, { key: 'actor', label: 'who' }, { key: 'action', label: 'did' }, { key: '', label: 'detail' }];
  public readonly usageCols = [
    { key: 'key', label: '' }, { key: 'turns', label: 'turns', num: true }, { key: 'in', label: 'input', num: true }, { key: 'out', label: 'output', num: true },
    { key: 'cache', label: 'cached', num: true }, { key: '', label: 'models' }, { key: 'cost_usd', label: 'est. cost', num: true },
  ];
  setSort(table: 'activity' | 'actions' | 'usage', key: string): void {
    if (!key) return;
    const s = this.sort[table];
    if (s.key === key) s.dir = s.dir > 0 ? -1 : 1;
    else { s.key = key; s.dir = ['ts', 'in', 'out', 'cache', 'cost_usd', 'turns'].includes(key) ? -1 : 1; }
    this.sort[table] = { ...s };
  }
  private sortBy<T>(rows: T[], key: string, dir: 1 | -1): T[] {
    const val = (r: any) => { const v = r?.[key]; return v === null || v === undefined ? '' : v; };
    return [...rows].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
      return c * dir;
    });
  }
  get sortedAuditEntries(): any[] { return this.sortBy(this.auditEntries || [], this.sort.actions.key, this.sort.actions.dir); }
  get sortedUsageRows(): any[] { return this.sortBy(this.usage?.rows || [], this.sort.usage.key, this.sort.usage.dir); }

  get hasActivityFilter(): boolean { return !!this.activitySource || Object.values(this.activityFilter).some((v) => v.trim()); }
  clearActivityFilter(): void { this.activityFilter = { user: '', agent: '', channel: '', model: '' }; this.activitySource = ''; this.loadAudit(); }
  sourceLabel(src: string): string {
    if (!src) return '—';
    if (src.startsWith('backfill')) return 'history';
    return ({ channel_reply: 'channel reply', autonomous: 'autonomous', webapp: 'chat' } as Record<string, string>)[src] || src;
  }
  /** Activity rows for the table: filtered by source, with runs of identical autonomous
   *  turns (same person/agent/channel/model, back to back) folded into one expandable line. */
  private _groupsKey = '';
  private _groups: any[] = [];
  get activityGroups(): any[] {
    const filtered: any[] = (this.activity?.rows || []).filter((r: any) => !this.activitySource || (this.activitySource === 'backfill' ? String(r.source || '').startsWith('backfill') : r.source === this.activitySource));
    const rows = this.sortBy(filtered, this.sort.activity.key, this.sort.activity.dir);
    const key = `${rows.length}:${rows[0]?.ts}:${rows[rows.length - 1]?.ts}:${this.activitySource}:${this.sort.activity.key}:${this.sort.activity.dir}`;
    if (key === this._groupsKey) return this._groups;
    const out: any[] = [];
    for (const r of rows) {
      const last = out[out.length - 1];
      const sig = `${r.user}|${r.agent}|${r.channel || ''}|${r.model || ''}`;
      if (r.source === 'autonomous' && last && last.sig === sig) {
        last.rows.push(r); last.count++; last.in += r.in || 0; last.out += r.out || 0; last.cache += r.cache || 0; last.cost += r.cost_usd || 0; last.first = r.ts;
        last.kind = 'run';
        continue;
      }
      out.push(r.source === 'autonomous'
        ? { kind: 'row', row: r, sig, rows: [r], count: 1, in: r.in || 0, out: r.out || 0, cache: r.cache || 0, cost: r.cost_usd || 0, first: r.ts, last: r.ts,
            user: r.user, agent: r.agent, channel: r.channel, model: r.model, open: false }
        : { kind: 'row', row: r, sig: '' });
    }
    this._groupsKey = key; this._groups = out;
    return out;
  }
  get activityShown(): { turns: number; cost: number; people: number; runs: number } {
    const rows: any[] = (this.activity?.rows || []);
    const shown = this.activityGroups;
    const turns = shown.reduce((n, g) => n + (g.kind === 'run' ? g.count : 1), 0);
    const cost = shown.reduce((n, g) => n + (g.kind === 'run' ? g.cost : (g.row.cost_usd || 0)), 0);
    return { turns, cost, people: new Set(rows.map((r) => r.user)).size, runs: shown.filter((g) => g.kind === 'run').length };
  }
  fmtTime(ts: number | string): string {
    const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  actionIcon(a: string): string {
    return ({ policy_saved: 'save', policy_removed: 'delete', policy_applied: 'sync', support_view: 'visibility', support_edit: 'edit',
      budget_alert: 'payments', model_switched: 'alt_route', agent_stopped: 'stop_circle', team_stopped: 'report', offboard: 'person_off', offboard_archived: 'inventory_2',
      offboard_reinstated: 'person_add', transcript_grant: 'key', transcript_view: 'menu_book', transcript_revoked: 'key_off', retention_pruned: 'auto_delete' } as Record<string, string>)[a] || 'history';
  }
  actionLabel(e: any): string {
    const t = e.target_user ? ` ${e.target_user}` : '';
    const d = e.detail || {};
    switch (e.action) {
      case 'policy_saved': return d.created ? 'created the policy' : 'saved the policy';
      case 'policy_removed': return 'removed the policy';
      case 'policy_applied': return `applied the policy to live sessions (${d.restarted ?? 0} restarted)`;
      case 'support_view': return `viewed${t}'s agents`;
      case 'support_edit': return `changed${t}'s agent ${e.target || ''}`;
      case 'budget_alert': return `budget alert for${t}`;
      case 'model_switched': return `switched${t}'s turn to ${(e.detail || {}).to_model || (e.detail || {}).to_backend || 'the fallback'} (${(e.detail || {}).scope || 'budget'} budget at ${(e.detail || {}).pct ?? '100'}%)`;
      case 'agent_stopped': return `stopped${t}'s agent ${e.target || ''}`;
      case 'team_stopped': return 'stopped every agent in the team';
      case 'offboard': return `offboarded${t}` + (d.successor ? ` → ${d.successor}` : '');
      case 'offboard_archived': return `archived${t}'s leftovers`;
      case 'offboard_reinstated': return `reinstated${t}`;
      case 'transcript_grant': return `opened a transcript window on${t}` + (d.hours ? ` (${d.hours} h)` : '') + (d.quiet ? ' · quiet' : '');
      case 'transcript_view': return e.target ? `read${t}'s channel ${e.target}` : `listed${t}'s channels`;
      case 'transcript_revoked': return `closed the transcript window on${t}`;
      case 'retention_pruned': return `retention pruned ${d.audit_rows ?? 0} audit + ${d.usage_rows ?? 0} usage rows`;
      default: return e.action;
    }
  }
  async exportAudit(fmt: 'csv' | 'jsonl'): Promise<void> {
    try {
      const path = this.auditView === 'activity' ? 'usage/export' : 'audit/export';
      // The export is the list on screen: same range, same filters.
      const extra: Record<string, string> = { format: fmt };
      if (this.auditView === 'activity') {
        for (const [k, v] of Object.entries(this.activityFilter)) if (v.trim()) extra[k] = v.trim();
        if (this.activitySource) extra['source'] = this.activitySource;
      } else if (this.auditActionFilter) {
        extra['action'] = this.auditActionFilter;
      }
      const text = await lastValueFrom(this.http.get(this.mux(`/teams/${encodeURIComponent(this.team)}/${path}?${this.usageQuery(extra)}`), { responseType: 'text' }));
      const blob = new Blob([text], { type: fmt === 'csv' ? 'text/csv' : 'application/x-ndjson' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const w = this.usageWindow();
      a.download = `${this.auditView === 'activity' ? 'activity' : 'audit'}-${this.team}-${w ? w.since + '_' + w.until : 'all'}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e: any) {
      this.auditError = e?.error?.detail || e?.message || 'Export failed';
    }
  }
  fmtWhen(ts: number | string | null | undefined): string {
    if (ts === null || ts === undefined) return '—';
    const d = new Date(typeof ts === 'number' ? ts * 1000 : ts);
    return isNaN(d.getTime()) ? String(ts) : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  describeDetail(d: any): string {
    if (!d || typeof d !== 'object') return d ? String(d) : '';
    if (d.changed) return Object.entries<any>(d.changed).map(([k, v]) => `${k}: ${v.from ?? '—'} → ${v.to ?? '—'}`).join('; ');
    return Object.entries(d).filter(([k]) => k !== 'period').map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ');
  }
  // ---- usage tab -----------------------------------------------------------------
  /** The selected range as since/until (UTC calendar days, until inclusive). */
  usageWindow(): { since: string; until: string } | null {
    const day = (d: Date) => d.toISOString().slice(0, 10);
    const now = new Date();
    const today = day(now);
    const back = (n: number) => day(new Date(now.getTime() - n * 86400000));
    switch (this.usageRange) {
      case 'month': return { since: today.slice(0, 8) + '01', until: today };
      case '30d': return { since: back(29), until: today };
      case '90d': return { since: back(89), until: today };
      case 'year': return { since: today.slice(0, 5) + '01-01', until: today };
      case 'custom': return this.usageSince ? { since: this.usageSince, until: this.usageUntil || today } : null;
    }
  }
  setUsageRange(r: 'month' | '30d' | '90d' | 'year' | 'custom'): void {
    this.usageRange = r;
    if (r === 'custom') {
      if (!this.usageSince) { const w = this.usageWindow() || { since: '', until: '' }; this.usageSince = w.since; this.usageUntil = w.until; }
      return;   // wait for the dates
    }
    this.tab === 'audit' ? this.loadAudit() : this.loadUsage();
  }
  private usageQuery(extra: Record<string, string> = {}): string {
    const q = new URLSearchParams(extra);
    const w = this.usageWindow();
    if (w) { q.set('since', w.since); q.set('until', w.until); }
    return q.toString();
  }
  async loadUsage(): Promise<void> {
    if (this.usageRange === 'custom' && !this.usageSince) return;
    this.usageLoading = true; this.usageError = '';
    try {
      this.usage = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/usage?${this.usageQuery({ by: this.usageBy })}`)));
      // group budget cards need spend per person whatever breakdown is on screen
      const gb = this.budgetsBlock()?.groups || {};
      this.usageByUser = this.usageBy === 'user' ? this.usage
        : (Object.keys(gb).length && this.usageRange === 'month')
          ? await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/usage?${this.usageQuery({ by: 'user' })}`))).catch(() => null)
          : null;
    } catch (e: any) {
      this.usageError = e?.error?.detail || e?.message || 'Failed to load usage';
    } finally {
      this.usageLoading = false;
    }
  }
  /** The breakdown table exactly as shown: same grouping, same range, same sort. */
  exportUsageBreakdown(): void {
    const rows = this.sortedUsageRows;
    const esc = (v: any) => { const t = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
    const lines = [[this.usageBy, 'turns', 'input_tokens', 'output_tokens', 'cached_tokens', 'models', 'est_cost_usd'].join(',')];
    for (const r of rows) {
      const models = Object.entries<any>(r.models || {}).map(([m, n]) => `${m} x${n}`).join('; ');
      lines.push([r.key || '', r.turns, r.in, r.out, r.cache, models, r.cost_usd == null ? '' : Number(r.cost_usd).toFixed(4)].map(esc).join(','));
    }
    const t = this.usage?.totals || {};
    lines.push(['TOTAL', t.turns, t.in, t.out, t.cache, '', t.cost_usd == null ? '' : Number(t.cost_usd).toFixed(4)].map(esc).join(','));
    const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const w = this.usageWindow();
    a.download = `usage-by-${this.usageBy}-${this.team}-${w ? w.since + '_' + w.until : 'all'}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }
  async exportUsage(fmt: 'csv' | 'jsonl'): Promise<void> {
    try {
      const text = await lastValueFrom(this.http.get(this.mux(`/teams/${encodeURIComponent(this.team)}/usage/export?${this.usageQuery({ format: fmt })}`), { responseType: 'text' }));
      const blob = new Blob([text], { type: fmt === 'csv' ? 'text/csv' : 'application/x-ndjson' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const w = this.usageWindow();
      a.download = `usage-${this.team}-${w ? w.since + '_' + w.until : 'all'}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e: any) {
      this.usageError = e?.error?.detail || e?.message || 'Export failed';
    }
  }
  fmtTok(n: number | null | undefined): string {
    const v = n || 0;
    if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(v >= 10_000_000_000 ? 0 : 1) + 'B';
    if (v >= 1_000_000) return (v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1) + 'M';
    if (v >= 1_000) return (v / 1_000).toFixed(v >= 10_000 ? 0 : 1) + 'k';
    return String(v);
  }
  fmtUsd(n: number | null | undefined): string {
    if (n === null || n === undefined) return '—';
    if (n >= 100) return '$' + Math.round(n).toLocaleString();
    return n >= 1 ? '$' + n.toFixed(2) : '$' + n.toFixed(3);
  }
  /** Access tab panels: the core three open, the rarely-touched ones folded. */
  public panels: Record<string, boolean> = { providers: true, groups: true, slots: true, integrations: false, agents: false, data: false, offboarding: false, network: false };
  private readonly panelById: Record<string, string> = { 'tp-providers': 'providers', 'tp-groups': 'groups', 'tp-slots': 'slots', 'tp-integrations': 'integrations',
    'tp-agent-rules': 'agents', 'tp-data': 'data', 'tp-offboarding': 'offboarding', 'tp-network': 'network' };
  jump(target?: string): void {
    if (!target) return;
    const key = this.panelById[target];
    if (key) this.panels[key] = true;
    setTimeout(() => {
      const el = (this._elRef.nativeElement as HTMLElement).querySelector(`#${target}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, key ? 120 : 0);
  }
  selectTab(t: any): void {
    this.tab = t; this._tabChosen = true;
    if (t === 'usage') this.loadUsage();
    if (t === 'audit') this.loadAudit();
  }
  // -- one-line summaries for the folded panels --
  get providersSummary(): string {
    const on = this.vendors.filter((v) => this.vendorEnabled(v));
    if (!on.length) return 'no providers enabled';
    const parts = on.map((v) => (this.vendorLabels[v] || v).split(' (')[0] + (this.isRestricted(v) ? ' (some models)' : ''));
    return parts.join(', ');
  }
  get groupsSummary(): string {
    const names = this.groupNames;
    return names.length ? names.join(', ') : 'none — everyone gets the defaults';
  }
  get slotsSummary(): string {
    const ids = this.slotIds;
    const own = this.memberOwnedAllowed ? 'members may add their own' : 'team credentials only';
    return ids.length ? `${ids.join(', ')} · ${own}` : `none named · ${own}`;
  }
  get integrationsSummary(): string {
    const allow = this.mcpAllowed === null ? 'any integration' : (this.mcpAllowed.length ? `only ${this.mcpAllowed.join(', ')}` : 'none');
    return this.mcpDenied.length ? `${allow} · never ${this.mcpDenied.join(', ')}` : allow;
  }
  get agentRulesSummary(): string {
    const editable = Object.values(this.memberEditable).filter(Boolean).length;
    const prov = Object.values(this.defaults).filter(Boolean).length;
    return `created by ${this.provisionedBy === 'admin' ? 'admins only' : 'any member'} · default scope ${this.defaultScopeMode} · members edit ${editable} of ${this.editableGroups.length} things` + (prov ? ` · ${prov} provisioned to everyone` : '');
  }
  get dataSummary(): string {
    const n = this.excludedPathList().length;
    return n ? `${n} pattern${n === 1 ? '' : 's'}: ${this.excludedPathList().slice(0, 3).join(', ')}${n > 3 ? '…' : ''}` : 'nothing excluded';
  }
  get offboardingSummary(): string {
    const o = this.offboarding;
    const what = [o.agents ? 'agents' : '', o.channels ? 'channels' : ''].filter(Boolean).join(' + ') || 'nothing';
    const named = this.successors.filter((r) => r.member.trim() && r.successor.trim()).length;
    const who = o.successor.trim() ? `${what} → ${o.successor.trim()}` : (named ? `${what} → nobody by default` : '');
    if (!who) return 'no successor named';
    return `${who}${named ? ` · ${named} named individually` : ''} · archive after ${o.archiveDays || 30} days${o.idp ? ' · IdP-driven' : ''}`;
  }
  get networkSummary(): string {
    const parts = [];
    if (this.telemetry.endpoint.trim()) parts.push(`telemetry → ${this.telemetry.endpoint.trim()}`);
    if (this.network.httpsProxy.trim()) parts.push(`proxy ${this.network.httpsProxy.trim()}`);
    if (this.network.anthropicBaseUrl.trim() || this.network.openaiBaseUrl.trim()) parts.push('gateway base URLs set');
    return parts.length ? parts.join(' · ') : 'direct to the vendors, no telemetry export';
  }

  get allUnrestricted(): boolean { const p = this.preview; return p.length > 0 && p.every((x) => x.unrestricted); }

  get groupNames(): string[] { return this.groups.map((g) => g.name.trim()).filter(Boolean); }
  get meName(): string { return this.identity.me?.username || ''; }
  /** Members tab: the connected column only earns its place once team credentials are named. */
  get hasSlots(): boolean { return this.teamMembers.some((m) => m.slots && Object.keys(m.slots).length); }
  get policyFileNames(): string {
    const named = this.teamMembers.filter((m) => m.role_source === 'policy' && (m.role === 'owner' || m.role === 'admin'));
    return named.map((m) => `${m.username} as ${m.role}`).join(', ');
  }

  auditBlock(): any | null {
    const out: any = {};
    const rd = parseInt(this.retainDays, 10);
    if (rd > 0) out.retain_days = rd;
    const gh = parseInt(this.grantMaxHours, 10);
    if (gh > 0 && gh !== 72) out.transcript_grant_max_hours = gh;
    const holds = this.holds.filter((h) => h.user.trim()).map((h) => ({
      user: h.user.trim(), reason: h.reason.trim(), since: h.since || Math.floor(Date.now() / 1000), by: h.by || this.meName }));
    if (holds.length) out.holds = holds;
    return Object.keys(out).length ? out : null;
  }
  addHold(user = ''): void { this.holds.push({ user, reason: '' }); }
  removeHold(i: number): void { this.holds.splice(i, 1); }

  /** Transcript access (3.3) — the support console's grant + viewer for the selected member. */
  async loadGrant(): Promise<void> {
    this.grant = null; this.transcriptChannels = []; this.transcriptChannel = ''; this.transcriptMessages = []; this.transcriptNotice = '';
    if (!this.supportMember || this.supportMember === this.meName) return;
    try {
      const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/transcripts/grant`)));
      this.grant = res?.grant || null;
      this.grantMeta = { max_hours: res?.max_hours || 72, can_quiet: !!res?.can_quiet };
      if (this.grant) await this.loadTranscriptChannels();
    } catch { /* not an admin, or no team */ }
  }
  async openGrant(): Promise<void> {
    if (!this.grantForm.reason.trim()) { this.transcriptNotice = 'A reason is required — it goes in the audit log and on the member\'s summary.'; return; }
    try {
      const res = await lastValueFrom(this.http.post<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/transcripts/grant`),
        { hours: parseFloat(this.grantForm.hours) || 4, reason: this.grantForm.reason.trim(), notify: this.grantForm.notify }));
      this.grant = res; this.grantForm.reason = '';
      this.transcriptNotice = `Window open until ${new Date(res.until * 1000).toLocaleString()} — every read is audited` + (res.quiet ? ' (quiet: not shown to the member)' : ` and ${this.supportMember} sees it on their summary`) + '.';
      await this.loadTranscriptChannels();
    } catch (e: any) { this.transcriptNotice = e?.error?.detail || e?.message || 'Grant failed'; }
  }
  async revokeGrant(): Promise<void> {
    try {
      await lastValueFrom(this.http.delete<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/transcripts/grant`)));
      this.grant = null; this.transcriptChannels = []; this.transcriptMessages = []; this.transcriptChannel = '';
      this.transcriptNotice = 'Window closed — recorded in the audit log.';
    } catch (e: any) { this.transcriptNotice = e?.error?.detail || e?.message || 'Revoke failed'; }
  }
  async loadTranscriptChannels(): Promise<void> {
    try {
      const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/transcripts`)));
      this.transcriptChannels = res?.channels || [];
    } catch (e: any) { this.transcriptNotice = e?.error?.detail || e?.message || 'Could not list channels'; }
  }
  async loadTranscript(): Promise<void> {
    if (!this.transcriptChannel) return;
    this.transcriptLoading = true; this.transcriptMessages = [];
    try {
      const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/transcripts?channel=${encodeURIComponent(this.transcriptChannel)}&limit=50`)));
      this.transcriptMessages = res?.messages || [];
    } catch (e: any) { this.transcriptNotice = e?.error?.detail || e?.message || 'Could not read the channel'; }
    finally { this.transcriptLoading = false; }
  }

  excludedPathList(): string[] {
    return this.excludedPaths.split(/\r?\n|,/).map((x) => x.trim()).filter(Boolean);
  }
  offboardingBlock(): any | null {
    const o = this.offboarding;
    const transfer = [...(o.agents ? ['agents'] : []), ...(o.channels ? ['channels'] : [])];
    const days = Math.max(0, parseInt(o.archiveDays, 10) || 0);
    const successors: Record<string, string> = {};
    for (const r of this.successors) if (r.member.trim() && r.successor.trim() && r.member.trim() !== r.successor.trim()) successors[r.member.trim()] = r.successor.trim();
    const isDefault = !o.successor.trim() && !Object.keys(successors).length && o.agents && o.channels && days === 30 && o.idp;
    if (isDefault) return null;
    return { ...(o.successor.trim() ? { successor: o.successor.trim() } : {}), ...(Object.keys(successors).length ? { successors } : {}),
             transfer, archive_after_days: days, idp: o.idp };
  }
  addSuccessor(member = ''): void { this.successors.push({ member, successor: '' }); }
  removeSuccessor(i: number): void { this.successors.splice(i, 1); }
  successorOf(member: string): string { return this.successors.find((r) => r.member === member)?.successor || ''; }
  /** Members-tab shortcut: name (or clear) one person's successor and save the policy right away. */
  async setSuccessorFor(m: any): Promise<void> {
    const current = this.successorOf(m.username);
    const v = await this.dialogs.prompt(`Successor for ${m.username} — the member or group who receives their agents and channels if they leave.\nBlank = follow the team-wide successor${this.offboarding.successor.trim() ? ` (${this.offboarding.successor.trim()})` : ' (none named)'}.`, current);
    if (v === null) return;
    const val = v.trim();
    if (val === m.username) { this.error = 'Someone cannot be their own successor.'; return; }
    this.successors = this.successors.filter((r) => r.member !== m.username);
    if (val) this.successors.push({ member: m.username, successor: val });
    await this.save();
    this.notice = val ? `If ${m.username} leaves, their work goes to ${val}.` : `${m.username} now follows the team-wide successor.`;
  }
  /** Offboarding (3.1): stop, revoke, transfer to the successor, archive the rest later. */
  async offboardMember(m: any): Promise<void> {
    const suggested = this.offboarding.successor.trim();
    const successor = await this.dialogs.prompt(`Offboard ${m.username} from ${this.team}?\n\nTheir agents stop now, their team access and credential files go, and their agents/channels move to a successor (heartbeats off on arrival). What isn't moved is archived after ${this.offboarding.archiveDays || 30} days.\n\nSuccessor (a member, or a group name — blank = nothing transfers):`, suggested);
    if (successor === null) return;
    const typed = await this.dialogs.prompt(`Type ${m.username} to confirm:`);
    if (typed !== m.username) return;
    try {
      const res = await lastValueFrom(this.http.post<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(m.username)}/offboard`),
        { confirm: m.username, ...(successor.trim() ? { successor: successor.trim() } : {}) }));
      const t = res?.transferred || { agents: [], channels: [] };
      this.notice = `${m.username} offboarded: ${res?.agents_stopped ?? 0} agent(s) stopped, ${(t.agents || []).length} agent(s) + ${(t.channels || []).length} channel(s) → ${res?.successor || 'nobody'}`
        + (res?.errors?.length ? ` — ${res.errors.length} step(s) failed: ${res.errors.join('; ')}` : '') + '. Recorded in the audit log.';
      await this.loadMembers();
    } catch (e: any) {
      this.error = e?.error?.detail || e?.message || 'Offboarding failed';
    }
  }
  async reinstateMember(m: any): Promise<void> {
    if (!await this.dialogs.confirm(`Reinstate ${m.username}? Their team access returns; anything already moved to ${m.offboarded?.successor || 'a successor'} stays there.`)) return;
    try {
      await lastValueFrom(this.http.post<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(m.username)}/reinstate`), {}));
      this.notice = `${m.username} reinstated — recorded in the audit log.`;
      await this.loadMembers();
    } catch (e: any) {
      this.error = e?.error?.detail || e?.message || 'Reinstate failed';
    }
  }

  /** The policy file's own role entries (lockout safety net) — shown read-only. */
  get policyNamedRoles(): string {
    return this.members.filter((m) => m.role !== 'member' && m.username.trim())
      .map((m) => `${m.username} as ${m.role}`).join(', ');
  }

  /**
   * What a member of `viewAs` (or of no group) can pick — mirrors
   * policy.py effective_models: union of the group's allows, else the team
   * default; minus its denies; deny always wins. Returned per vendor as
   * family chips with coverage counts.
   */
  get preview(): { vendor: string; label: string; families: { label: string; n: number; of: number }[]; unrestricted: boolean }[] {
    const g = this.groups.find((x) => x.name.trim() === this.viewAs);
    const out = [];
    for (const v of this.vendors) {
      if (!this.vendorEnabled(v)) continue;
      const allows = g?.allow[v]?.length ? g.allow[v] : null;
      const allowed: string[] | null = allows ?? this.defaultAccess[v] ?? null;
      const denies = g?.deny[v] || [];
      const ids = this.modelsByVendor[v].filter((id) => !modelIn(id, denies) && (allowed === null || modelIn(id, allowed)));
      const families = this.familiesByVendor[v]
        .map((f) => ({ label: f.label, n: f.ids.filter((id) => ids.includes(id)).length, of: f.ids.length }))
        .filter((f) => f.n > 0);
      out.push({ vendor: v, label: VENDOR_LABELS[v] || v, families, unrestricted: allowed === null && !denies.length });
    }
    return out;
  }

  // ---- row helpers -------------------------------------------------------------

  addGroup(): void {
    const row: GroupRow = { name: '', members: '', allow: {}, deny: {}, agents: {}, dirs: '', slotsAllow: [], slotsDeny: [], mcpAllow: [], mcpDeny: [], more: false };
    for (const v of this.vendors) { row.allow[v] = []; row.deny[v] = []; }
    this.groups.push(row);
  }
  removeGroup(i: number): void { this.groups.splice(i, 1); }
  async loadTeamKeys(): Promise<void> {
    try {
      const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/credentials`)));
      this.teamKeys = res?.credentials || {};
    } catch { this.teamKeys = {}; }
  }
  slotSaved(s: SlotRow): boolean { return this.savedSlotIds.includes(s.id.trim()); }
  async setTeamKey(s: SlotRow): Promise<void> {
    const key = await this.dialogs.prompt(`API key for ${s.id} (${this.vendorLabels[s.vendor] || s.vendor}).\n\nStored service-side, never shown again, injected for every member's agent bound to this credential. Recorded in the audit log without the key.`);
    if (key === null) return;
    try {
      await lastValueFrom(this.http.put<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/credentials/${encodeURIComponent(s.id.trim())}`), { api_key: key.trim() }));
      this.notice = `Key set for ${s.id} — members bound to it can dispatch now.`;
      await this.loadTeamKeys(); this.loadMembers();
    } catch (e: any) { this.error = e?.error?.detail || e?.message || 'Could not set the key'; }
  }
  async clearTeamKey(s: SlotRow): Promise<void> {
    if (!await this.dialogs.confirm(`Remove the key behind ${s.id}? Agents bound to it stop dispatching until a new key is set.`)) return;
    try {
      await lastValueFrom(this.http.delete<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/credentials/${encodeURIComponent(s.id.trim())}`)));
      this.notice = `Key removed for ${s.id}.`;
      await this.loadTeamKeys(); this.loadMembers();
    } catch (e: any) { this.error = e?.error?.detail || e?.message || 'Could not remove the key'; }
  }
  addSlot(): void {
    const used = new Set(this.slots.map((s) => s.vendor));
    const vendor = this.vendors.find((v) => !used.has(v)) || this.vendors[0] || '';
    const n = this.slots.filter((s) => s.vendor === vendor).length;
    this.slots.push({ id: vendor ? `${vendor}-seat${n ? '-' + (n + 1) : ''}` : '', vendor, mode: 'oauth', holder: 'user', required: false, default: n === 0 });
  }
  removeSlot(i: number): void {
    const [gone] = this.slots.splice(i, 1);
    const rest = this.slots.filter((s) => s.vendor === gone.vendor);
    if (gone.default && rest.length) rest[0].default = true;
  }
  setDefault(i: number): void { for (const s of this.slots) if (s.vendor === this.slots[i].vendor) s.default = false; this.slots[i].default = true; }
  onSlotVendorChange(i: number): void {
    const s = this.slots[i];
    s.default = !this.slots.some((x, j) => j !== i && x.vendor === s.vendor && x.default);
  }
  get slotIds(): string[] { return this.slots.map((s) => s.id.trim()).filter(Boolean); }
  addMember(): void { this.members.push({ username: '', role: 'member' }); }
  removeMember(i: number): void { this.members.splice(i, 1); }

  // ---- support console ---------------------------------------------------------

  /** Lazy per-row health: issue count across the member's agents. */
  async loadMemberHealth(): Promise<void> {
    for (const m of this.teamMembers) {
      try {
        const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(m.username)}/agents`)));
        const agents = res?.agents || [];
        m.health = { agents: agents.length, issues: agents.reduce((n: number, a: any) => n + (a.issues?.length || 0), 0) };
      } catch { m.health = null; }
      // default change detection picks the row updates up
    }
  }

  openAgentsFor(username: string): void {
    this.supportMember = username;
    this.tab = 'agents';
    this.loadMemberAgents();
  }

  async loadMembers(): Promise<void> {
    try {
      const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members`)));
      this.teamMembers = res?.members || [];
      this.loadMemberHealth();          // fills in after the rows render
      if (!this.supportMember && this.teamMembers.length) {
        const meName = this.identity.me?.username;
        this.supportMember = (this.teamMembers.find((m) => m.username === meName) || this.teamMembers[0]).username;
        await this.loadMemberAgents();
      }
    } catch { this.teamMembers = []; }
  }

  async loadMemberAgents(): Promise<void> {
    if (!this.supportMember) return;
    this.loadGrant();                   // transcript window state rides alongside (3.3)
    this.supportLoading = true; this.supportNotice = '';
    try {
      const res = await lastValueFrom(this.http.get<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/agents`)));
      this.supportAgents = (res?.agents || []).map((a: any) => ({
        ...a, edit: false, showPrompt: false,
        draft: {
          model: a.model || '', credentials_id: a.credentials_id || '',
          mcp_servers: (a.mcp_servers || []).join(', '),
          mcpList: [...(a.mcp_servers || [])],
          system_prompt: a.system_prompt || '', temperature: a.temperature,
        },
      }));
      this.supportSelected = this.supportSelected ? this.supportAgents.find((x: any) => x.id === this.supportSelected.id) || null : null;
    } catch (e: any) {
      this.supportAgents = [];
      this.error = e?.error?.detail || e?.message || 'Failed to load member agents';
    } finally {
      this.supportLoading = false;
    }
  }

  /** Per-agent scope lives in policy.json (keyed by agent id) — setting it from
   *  the support console PUTs the policy immediately, like the other controls. */
  async setAgentScope(a: any, mode: string): Promise<void> {
    if (!this.agentScopes[a.id]) this.agentScopes[a.id] = { mode: '', mcp: '', tools: '', dirs: 'channel-workspace' };
    this.agentScopes[a.id].mode = mode;
    await this.save();
    this.supportNotice = mode
      ? `Scope for ${a.name || a.id} is now ${mode} — applies on their next message.`
      : `Scope for ${a.name || a.id} follows the team default (${this.defaultScopeMode}).`;
  }

  /** Kill switch (2.5): stop one agent now and keep it stopped until an admin re-enables its heartbeat. */
  async stopAgent(a: any): Promise<void> {
    if (!this.supportMember) return;
    if (!await this.dialogs.confirm(`Stop ${a.name || a.id} now? Its live session ends, its heartbeat stays off until you turn it back on, and ${this.supportMember} will see this on their summary.`)) return;
    try {
      const res = await lastValueFrom(this.http.post<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/agents/${encodeURIComponent(a.id)}/stop`), {}));
      this.supportNotice = `Stopped ${a.name || a.id} (${res?.sessions_killed ?? 0} live session(s) killed) — recorded in the audit log.`;
      await this.loadMemberAgents();
    } catch (e: any) {
      this.supportNotice = e?.error?.detail || e?.message || 'Stop failed';
    }
  }
  /** Support console (2026-09-10): restart every live session of one member — the wedged-bridge case
   *  they can't diagnose. Sessions resume on their next message. Audited server-side. */
  async restartMemberSessions(member: string): Promise<void> {
    if (!member) return;
    if (!await this.dialogs.confirm(`Restart every live session of ${member}? Their conversations resume on their next message.`)) return;
    try {
      const res = await lastValueFrom(this.http.post<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(member)}/sessions/restart-all`), {}));
      const n = res?.killed ?? 0;
      const msg = `Restarted ${n} session${n === 1 ? '' : 's'} for ${member}` + (res?.daemons_killed ? ` (${res.daemons_killed} daemon(s) killed)` : '') + ' — recorded in the audit log.';
      if (this.tab === 'agents' && this.supportMember === member) { this.supportNotice = msg; await this.loadMemberAgents(); }
      else this.notice = msg;
    } catch (e: any) {
      const err = e?.error?.detail || e?.message || 'Restart failed';
      if (this.tab === 'agents') this.supportNotice = err; else this.error = err;
    }
  }
  /** Kill switch for the whole team. */
  async stopTeam(): Promise<void> {
    const typed = await this.dialogs.prompt(`Stop EVERY agent in ${this.team} now? Live sessions end and every heartbeat stays off until re-enabled per agent. Type the team name to confirm:`);
    if (typed !== this.team) return;
    try {
      const res = await lastValueFrom(this.http.post<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/stop`), { confirm: this.team }));
      this.notice = `Stopped ${res?.stopped ?? 0} agent(s), ${res?.sessions_killed ?? 0} live session(s) killed — recorded in the audit log.`;
      this.loadMembers();
    } catch (e: any) {
      this.error = e?.error?.detail || e?.message || 'Team stop failed';
    }
  }
  async toggleHeartbeat(a: any): Promise<void> {
    await this.patchMemberAgent(a, { heartbeat_enabled: !a.heartbeat_enabled },
      `${a.heartbeat_enabled ? 'Stopped' : 'Started'} the heartbeat on ${a.name || a.id}`);
  }

  /** Integrations the console may attach to this member's agent: every server the team knows,
   *  plus whatever the agent already has (so an unknown name can still be removed). */
  mcpOptionsFor(a: any): string[] {
    return Array.from(new Set([...this.mcpNames, ...(a.mcp_servers || [])])).sort();
  }
  async onMcpChange(a: any, kind: 'added' | 'removed' | 'reordered', ev: any): Promise<void> {
    if (kind === 'removed') a.draft.mcpList = (a.draft.mcpList || []).filter((x: string) => x !== ev.name);
    const next: string[] = [...(a.draft.mcpList || [])];
    if (next.join(',') === (a.mcp_servers || []).join(',')) return;
    await this.patchMemberAgent(a, { mcp_servers: next }, `Updated ${a.name || a.id}'s integrations for ${this.supportMember}`);
  }
  async saveMemberAgent(a: any): Promise<void> {
    const body: any = {};
    if ((a.draft.model || '') !== (a.model || '')) body.model = a.draft.model;
    if ((a.draft.credentials_id || '') !== (a.credentials_id || '')) body.credentials_id = a.draft.credentials_id;
    const mcp = this.splitList(a.draft.mcp_servers);
    if (mcp.join(',') !== (a.mcp_servers || []).join(',')) body.mcp_servers = mcp;
    if (!a.system_prompt_file && (a.draft.system_prompt || '') !== (a.system_prompt || '')) body.system_prompt = a.draft.system_prompt;
    if (a.draft.temperature !== a.temperature && a.draft.temperature !== null && a.draft.temperature !== undefined) body.temperature = Number(a.draft.temperature);
    if (!Object.keys(body).length) { a.edit = false; return; }
    await this.patchMemberAgent(a, body, `Updated ${a.name || a.id} for ${this.supportMember}`);
  }

  private async patchMemberAgent(a: any, body: any, what: string): Promise<void> {
    try {
      await lastValueFrom(this.http.patch<any>(this.mux(`/teams/${encodeURIComponent(this.team)}/members/${encodeURIComponent(this.supportMember)}/agents/${encodeURIComponent(a.id)}`), body));
      this.supportNotice = `${what} — recorded in the audit log; they'll see it on their summary.`;
      await this.loadMemberAgents();
    } catch (e: any) {
      this.error = e?.error?.detail || e?.message || 'Save failed';
    }
  }

  private splitList(s: string): string[] { return (s || '').split(',').map((x) => x.trim()).filter(Boolean); }
  private canon(v: string): string { return ({ claude: 'anthropic', gemini: 'google' } as any)[v] || v; }
  private alias(v: string): string { return ({ anthropic: 'claude', google: 'gemini' } as any)[v] || v; }
  public trackByIndex(i: number): number { return i; }
}
